import type { WatchlistItem, SortColumn, SortDirection, WatchlistStats } from '../types';

// Colors for checklist badges - cycle through these
export const CHECKLIST_COLORS = [
  '#0077B6', // IKEA blue
  '#22c55e', // green
  '#8b5cf6', // purple
  '#f59e0b', // amber
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#ef4444', // red
  '#84cc16', // lime
];

// Get consistent color for a checklist based on its ID
export const getChecklistColor = (checklistId: string, allChecklistIds: string[]): string => {
  const index = allChecklistIds.indexOf(checklistId);
  return CHECKLIST_COLORS[index % CHECKLIST_COLORS.length];
};

// Get all unique checklist IDs for consistent coloring
export const getAllChecklistIds = (items: WatchlistItem[]): string[] => {
  const ids = new Set<string>();
  items.forEach(item => {
    item.checklists?.forEach(c => ids.add(c.id));
  });
  return Array.from(ids).sort();
};

// Get total quantity needed from all checklists
export const getTotalQty = (item: WatchlistItem): number => {
  if (!item.checklists?.length) return 1;
  return item.checklists.reduce((sum, c) => sum + (c.quantity_needed || 1), 0);
};

// Calculate stats for hero section
export const calculateStats = (items: WatchlistItem[]): WatchlistStats => {
  return {
    totalTracking: items.length,
    totalQtyNeeded: items.reduce((sum, item) => sum + getTotalQty(item), 0),
    priceDrops: items.filter(item =>
      item.originalPrice != null &&
      item.currentPrice != null &&
      item.currentPrice < item.originalPrice
    ).length,
    inStock: items.filter(item => item.availability === 'In Stock').length,
    lowStock: items.filter(item => item.availability === 'Low Stock').length,
  };
};

// Filter items by search query
export const filterItems = (items: WatchlistItem[], filter: string): WatchlistItem[] => {
  if (!filter.trim()) return items;

  const lowerFilter = filter.toLowerCase();
  return items.filter(item =>
    item.name.toLowerCase().includes(lowerFilter) ||
    item.productCode.includes(filter) ||
    item.checklists?.some(c => c.name.toLowerCase().includes(lowerFilter))
  );
};

// Sort items by column and direction
export const sortItems = (
  items: WatchlistItem[],
  column: SortColumn,
  direction: SortDirection
): WatchlistItem[] => {
  return [...items].sort((a, b) => {
    let comparison = 0;

    switch (column) {
      case 'qty':
        comparison = getTotalQty(a) - getTotalQty(b);
        break;
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'price':
        comparison = (a.currentPrice || 0) - (b.currentPrice || 0);
        break;
      case 'status':
        comparison = (a.availability || '').localeCompare(b.availability || '');
        break;
      case 'source': {
        // Sort by first checklist name, or "ZZZZ" for manual (goes last)
        const aSource = a.checklists?.[0]?.name || 'ZZZZ';
        const bSource = b.checklists?.[0]?.name || 'ZZZZ';
        comparison = aSource.localeCompare(bSource);
        break;
      }
    }

    return direction === 'asc' ? comparison : -comparison;
  });
};

// Process items with filter and sort
export const processItems = (
  items: WatchlistItem[],
  filter: string,
  column: SortColumn,
  direction: SortDirection
): WatchlistItem[] => {
  const filtered = filterItems(items, filter);
  return sortItems(filtered, column, direction);
};

// Open IKEA product page
export const openProductPage = (productCode: string): void => {
  const codeWithoutDots = productCode.replace(/\./g, '');
  window.open(`https://www.ikea.com/us/en/p/-${codeWithoutDots}/`, '_blank');
};

// Get store monitoring display text
export const getStoreMonitoringText = (stores: string[], t: (key: string, fallback: string) => string): string => {
  if (stores.length === 0 || stores.includes('all')) {
    return t('watchlist.allStores', 'All stores');
  }
  const count = stores.length;
  return count === 1
    ? `1 ${t('watchlist.store', 'store')}`
    : `${count} ${t('watchlist.stores', 'stores')}`;
};
